import { v4 } from 'uuid';

export class AsyncWorker<T, K> {
  private readonly promises: Map<
    string,
    { resolve: (value: any) => void; reject: (reason?: any) => void }
  > = new Map();

  constructor(private readonly worker: Worker) {
    worker.onmessage = (e: MessageEvent<WorkerMessage<any>>) => {
      const { id, data, error } = e.data;
      const promise = this.promises.get(id);
      if (promise) {
        this.promises.delete(id);
        if (error) {
          return promise.reject(error);
        }
        return promise.resolve(data);
      }
      throw new Error(`No promise found for id ${id}`);
    };
  }

  send(data: T): Promise<K> {
    const id = v4();
    const p = new Promise<K>((resolve, reject) => {
      this.promises.set(id, { resolve, reject });
    });
    this.worker.postMessage({ data, id });

    return p;
  }
}

export class WorkerPool<T, K> implements Pick<AsyncWorker<T, K>, 'send'> {
  private readonly workers: AsyncWorker<T, K>[] = [];
  private currentWorker: number = 0;

  constructor(workerFactory: () => Worker, opts: { poolSize: number }) {
    for (let i = 0; i < opts.poolSize; i++) {
      this.workers.push(new AsyncWorker(workerFactory()));
    }
  }

  send(data: T): Promise<K> {
    const worker = this.workers[this.currentWorker]!;
    this.currentWorker = (this.currentWorker + 1) % this.workers.length;
    return worker.send(data);
  }
}

export type WorkerMessage<T> = {
  id: string;
  data: T;
  error?: Error;
};
